// Static subject metadata. Dynamic numbers (respondent counts, brand awareness
// percentages, qual themes, quotes) come from the ETL JSON via lib/realdata.ts.
// Anything that remains here is either truly static (name, city list) or a
// placeholder the demo can still use until a later ETL pass grounds it.
export const subject = {
  name: "Mixer Grinder",
  code: "MG",
  waves: 2,
  // Respondents per wave come from ETL (MgQuant.meta.waves[].n_total).
  // Keep these as fallbacks that match the real counts we produced.
  wave1: 1491,
  wave2: 742,
  respondents: 2233,
  qualDIs: 18,
  cities: ["Kolkata", "Lucknow", "Gorakpur"] as const,
  variables: 3490,
  harmonised: 412,
  socialVolume90d: 41200,
};

export const brands = [
  { name: "Preethi", w1: 36, w2: 44 },
  { name: "Bajaj",   w1: 28, w2: 27 },
  { name: "Philips", w1: 22, w2: 25 },
  { name: "Sujata",  w1: 18, w2: 20 },
  { name: "Usha",    w1: 14, w2: 16 },
  { name: "Others",  w1: 8,  w2: 10 },
];

export const otherSubjects = [
  { name: "OX Category",          tag: "Hypothesis · 1 Wave" },
  { name: "Tata Nexon",           tag: "Placeholder" },
  { name: "Mahindra XUV 3XO",     tag: "Placeholder" },
  { name: "Britannia Good Day",   tag: "Placeholder" },
  { name: "ITC Bingo!",           tag: "Placeholder" },
];

// -------------------------------------------------------------------
// ASK SCENARIOS — one per suggestion chip. Each tells a different
// kind of story (quant trend, qual synthesis, segmentation,
// triangulation) so the demo feels like Pulse can handle anything.
// -------------------------------------------------------------------

export type Citation = {
  n: number;
  kind: "Quant" | "Qual" | "Social";
  label: string;
  // Scripted transcript (used for the v1-style Papiya citation):
  detailTitle?: string;
  detailLines?: { who: "M" | "R"; text: string; mark?: boolean }[];
  // Or pull real quotes from mg_quote_bank.json at render time by theme:
  quoteTheme?: string;
  quoteCity?: string; // optional city filter
};

export type ChartType = "trend" | "qualBars" | "segments" | "divergence" | null;

import type { ReactNode } from "react";

export type AskScenario = {
  id: string;
  question: string;
  shortLabel: string;             // what appears on the suggestion chip
  thinkingSteps: string[];
  headline: string;               // streamed out character-by-character
  headlineRich: ReactNode;        // rendered after streaming, with emphasis
  chart: ChartType;
  citations: Citation[];
  related?: string[];
};

// -------- Scenario 1 · Quant trend (wave-on-wave NPS) --------
const sc1: Omit<AskScenario, "headlineRich"> = {
  id: "nps-wave",
  question: "Compare Net Promoter Score for Preethi vs Bajaj among Mixer Grinder owners in Tier 2 cities, wave on wave.",
  shortLabel: "Compare NPS for Preethi vs Bajaj in Tier 2, wave on wave.",
  thinkingSteps: [
    "Reading 3,030 respondents across two waves…",
    "Filtering to Tier 2 cities: Kolkata, Lucknow, Gorakpur…",
    "Resolving Q42 (NPS) across both datamap versions…",
    "Cross-checking 18 qual transcripts for convergent evidence…",
    "Writing answer with citations…",
  ],
  headline: "Preethi's NPS rose +8 points wave-on-wave in Tier 2; Bajaj held flat. The movement is driven by Kolkata owners, not Lucknow or Gorakpur.",
  chart: "trend",
  citations: [
    { n: 1, kind: "Quant", label: "Q42 · NPS · Wave 1 & 2" },
    { n: 2, kind: "Quant", label: "Filter · Tier 2 · 3 cities" },
    {
      n: 3, kind: "Qual", label: "DI 3 · Papiya · Kolkata",
      detailTitle: "DI 3 · Papiya with moderator Shalini · 34 min",
      detailLines: [
        { who: "M", text: "How has your experience been with the mixer you bought last year?" },
        { who: "R", text: "See, when I bought it, I was so happy. The design was modern, the colour matched my kitchen. But after six months, the small jar cracked. I had to replace it. My neighbour also faced the same.", mark: true },
        { who: "M", text: "Would you still recommend it?" },
        { who: "R", text: "To family, yes, because the motor is strong. But I would warn them about the jars. This is what I tell everyone in my building.", mark: true },
      ],
    },
  ],
  related: [
    "Durability perception by city, Wave 2",
    "Jar-related mentions in qual, by brand",
    "Preethi Amazon reviews mentioning 'jar' (90d)",
  ],
};

// -------- Scenario 2 · Qual synthesis (cross-city theme frequencies) --------
const sc2: Omit<AskScenario, "headlineRich"> = {
  id: "durability-kol",
  question: "What are the dominant themes across the 18 depth interviews, and where do pain points cluster?",
  shortLabel: "What are the dominant themes in the qual interviews?",
  thinkingSteps: [
    "Opening all 18 depth interviews (3 cities · 3 moderators)…",
    "Extracting respondent turns and word counts…",
    "Tagging by 13 themes using keyword clusters…",
    "Aggregating respondents-mentioning and total-mentions…",
    "Pulling representative quotes per theme…",
  ],
  headline: "Aesthetics and design dominate the conversation (16 of 18 interviews). Among negative themes, jar/capacity concerns and after-sales service are the two that come up most across cities.",
  chart: "qualBars",
  // These citations pull real quotes from mg_quote_bank.json at render time.
  citations: [
    { n: 1, kind: "Qual", label: "18 DIs · 3 cities · theme counts" },
    { n: 2, kind: "Qual", label: "Jar cracking / breaking", quoteTheme: "Jar cracking / breaking" },
    { n: 3, kind: "Qual", label: "Service / after-sales",    quoteTheme: "Service / after-sales" },
    { n: 4, kind: "Qual", label: "Durability / longevity",   quoteTheme: "Durability / longevity" },
  ],
  related: [
    "How do themes split by city?",
    "Which brands get named alongside each pain point?",
    "Which themes shift between Wave 1 and Wave 2?",
  ],
};

// -------- Scenario 3 · Segmentation (psychographic intent) --------
const sc3: Omit<AskScenario, "headlineRich"> = {
  id: "segment-intent",
  question: "Which psychographic segment has the strongest intent to purchase in the next 6 months?",
  shortLabel: "Which psychographic segment has the strongest intent to purchase?",
  thinkingSteps: [
    "Loading the 14 psychographic factor scores from Wave 2…",
    "Resolving the 5 a-priori segments…",
    "Computing 6-month purchase intent by segment…",
    "Indexing against the category mean…",
    "Writing answer with segment definitions…",
  ],
  headline: "The 'Aspirational Organisers' segment is your strongest pool: 34% of them intend to buy in 6 months - roughly 3× the category average. They over-index on 'I plan major purchases carefully' and 'I research extensively before buying'.",
  chart: "segments",
  citations: [
    { n: 1, kind: "Quant", label: "Q38 · 6-month purchase intent" },
    { n: 2, kind: "Quant", label: "Psychographic factor scores · Wave 2" },
    { n: 3, kind: "Quant", label: "5 a-priori segments · Wave 2 segmentation" },
  ],
  related: [
    "Which brands do Aspirational Organisers prefer?",
    "Media habits of Aspirational Organisers",
    "Which cities over-index on this segment?",
  ],
};

// -------- Scenario 4 · Triangulation (social vs survey drift) --------
const sc4: Omit<AskScenario, "headlineRich"> = {
  id: "signal-drift",
  question: "Pull every Preethi mention from social where the tone disagrees with what the survey says.",
  shortLabel: "Pull every Preethi mention from social that doesn't match survey sentiment.",
  thinkingSteps: [
    "Pairing each social mention with a matched survey respondent slice…",
    "Scoring sentiment on both sides using the same rubric…",
    "Filtering to Preethi and pairs where the disagreement is significant…",
    "Cross-referencing against Pulse's flagged anomaly clusters…",
    "Writing answer with flagged cases…",
  ],
  headline: "412 Preethi mentions disagree with your survey. 76% are positive-social-but-negative-survey, concentrated in the 48-hour Kolkata review spike. Pulse recommends excluding these from any sentiment-weighted model.",
  chart: "divergence",
  citations: [
    { n: 1, kind: "Social", label: "Amazon + Twitter · 41.2K mentions · 90d" },
    { n: 2, kind: "Quant", label: "Q54 · Overall satisfaction · Wave 2" },
    { n: 3, kind: "Social", label: "Flagged anomaly · Kolkata · 2026-03-21" },
  ],
  related: [
    "Which other brands show the same pattern?",
    "Are the flagged reviews concentrated around specific SKUs?",
    "Has Amazon acted on any of these reviews?",
  ],
};

// Assemble with rich headlines (React elements for emphasis after streaming)
import { createElement as h } from "react";
function em(text: string, variant: "teal" | "warn" = "teal") {
  return h("em", { key: text, className: "em-" + variant, style: {
    fontStyle: "normal",
    color: variant === "teal" ? "var(--teal-ink)" : "var(--coral-ink)",
    background: variant === "teal" ? "var(--teal-tint)" : "var(--coral-tint)",
    padding: "0 5px",
    borderRadius: 4,
  } }, text);
}

export const askScenarios: Record<string, AskScenario> = {
  [sc1.id]: { ...sc1, headlineRich: h("span", null,
    "Preethi's NPS rose ", em("+8 points wave-on-wave"), " in Tier 2; Bajaj held flat. The movement is ", em("driven by Kolkata owners"), ", not Lucknow or Gorakpur.") },
  [sc2.id]: { ...sc2, headlineRich: h("span", null,
    em("Aesthetics and design"), " dominate the conversation (", em("16 of 18 interviews"), ") - respondents talk more about how the mixer looks in their kitchen than how it performs. Among negative themes, ", em("jar/capacity concerns", "warn"), " and ", em("after-sales service", "warn"), " are the two that come up most across cities.") },
  [sc3.id]: { ...sc3, headlineRich: h("span", null,
    "The ", em("'Aspirational Organisers'"), " segment is your strongest pool: ", em("34% intent"), " in 6 months - roughly ", em("3× the category average"), ". They over-index on 'I plan major purchases carefully' and 'I research extensively before buying'.") },
  [sc4.id]: { ...sc4, headlineRich: h("span", null,
    em("412 Preethi mentions"), " disagree with your survey. ", em("76% are positive-social-but-negative-survey"), ", concentrated in the ", em("48-hour Kolkata spike", "warn"), ". Pulse recommends excluding these from any sentiment-weighted model.") },
};

export const askSuggestionOrder: string[] = [sc1.id, sc2.id, sc3.id, sc4.id];

// -------------------------------------------------------------------
// Home / other screens
// -------------------------------------------------------------------
export const recentFeed = [
  {
    tag: "Investigation · Complete",
    title: "Preethi NPS drop — Q2 investigation",
    desc: "6 steps, 3 sources, confidence 0.92. Conclusion: durability issue in Lucknow/Gorakpur 25-34.",
    when: "2h ago",
    to: "/investigate",
  },
  {
    tag: "Signal · Flagged",
    title: "Amazon Kolkata — suspected review manipulation",
    desc: "84% of new reviews 5-star, 76% within 48h window. Generic language overlap.",
    when: "5h ago",
    to: "/signal",
  },
  {
    tag: "Ingestion · Harmonised",
    title: "MG Wave 2 landed — 37 brand codes reconciled",
    desc: "0 values dropped. Auditor AK approved the mapping.",
    when: "yesterday",
    to: "/repository",
  },
];

// Investigation steps (unchanged)
export type Step = {
  n: string;
  action: string;
  finding: string;
  mini?: "cityBars" | null;
};

export const investigationSteps: Step[] = [
  {
    n: "Step 01 · Confirm",
    action: "Re-computed Preethi NPS at the overall and Tier 2 level for both waves.",
    finding: "Overall NPS fell -6 points (+50 → +44). Tier 2 fell -8 points. Confirmed.",
  },
  {
    n: "Step 02 · Slice · Geography",
    action: "Sliced by city to locate concentration.",
    finding: "Drop concentrated in Lucknow (-14) and Gorakpur (-11). Kolkata stable at +49. The story is not national.",
    mini: "cityBars",
  },
  {
    n: "Step 03 · Slice · Demography",
    action: "Sliced Lucknow + Gorakpur owners by age.",
    finding: "The 25-34 segment accounts for 71% of the drop. 35-44 and 45+ segments essentially unchanged.",
  },
  {
    n: "Step 04 · Brand imagery",
    action: "Compared 14 brand-imagery attributes, Wave 2 vs Wave 1.",
    finding: "Durability fell 0.8 points. Modern design fell 0.4. All other attributes moved less than 0.2. Durability is the suspect.",
  },
  {
    n: "Step 05 · Qual cross-read",
    action: "Searched all 18 DIs for durability-adjacent language.",
    finding: "11 of 12 respondents in Lucknow and Gorakpur cited a jar crack or motor burn-out within 6-8 months of purchase. Only 1 of 6 in Kolkata.",
  },
  {
    n: "Step 06 · Social triangulation",
    action: "Scanned Amazon India reviews in the relevant window.",
    finding: "A 23% spike in 1-star and 2-star Preethi reviews citing 'jar broken' in the same quarter. Lucknow and Gorakpur PIN-code clusters over-represented.",
  },
];

export const signal = {
  quant: { score: 4.2, n: 512, delta: "Steady vs Wave 1 · no anomaly" },
  qual: {
    label: "Mixed, leaning concerned",
    quote: "The motor is very strong, I will not lie. But the jar, after six months, started to crack. Everyone in my building has faced this.",
    who: "Papiya · DI 3 · Kolkata · 34 min",
  },
  social: {
    score: 4.7,
    volume: 3118,
    before: 4.2,
    window: "6 days",
    share5star: 84,
    window48h: 76,
  },
};

export const reviews = [
  "Best mixer grinder, very nice quality, motor is good, recommended. Thanks seller.",
  "Very nice quality motor is good, I recommend this mixer grinder. Thank you seller.",
  "Quality is very nice, motor is good, thanks seller, recommended mixer grinder.",
  "Best product, nice quality, strong motor, recommend this seller, thank you.",
  "Nice quality mixer grinder, motor very strong, thank you seller, recommended.",
  "Very good mixer grinder, nice quality, strong motor, thanks seller.",
];
